// src/js-dot/api/http/functions/set-template-url.ts
import { isNone } from "@js-dot/core";
var _SEARCH_STRING = /((?:\/:\??[^/?#]+)*)(\?[^#]+)?/;
function setTemplateUrl(templateUrl, parameters = {}) {
  switch (typeof parameters) {
    case "string":
    case "number":
      throw new ReferenceError("Argument parameters should be object type");
  }
  if (!parameters) {
    parameters = {};
  }
  const defaultSearchStrings = templateUrl.match(/[?&][^=&#]+=[^&#]+/g);
  if (defaultSearchStrings) {
    let current = defaultSearchStrings.length;
    while (current-- > 0) {
      const searchString = defaultSearchStrings[current].split("=");
      const name = "$" + searchString.shift().slice(1);
      if (isNone(parameters[name])) {
        parameters[name] = searchString.join("=");
      }
    }
  }
  let templatedUrl = templateUrl.replace(_SEARCH_STRING, "$1");
  const searchStrings = [];
  for (const key in parameters) {
    if (parameters.hasOwnProperty(key) && key.startsWith("$")) {
      const value = parameters[key];
      if (!isNone(value)) {
        searchStrings[searchStrings.length] = key.slice(1) + "=" + value;
      }
    }
  }
  const pathVariables = templatedUrl.match(/(?::\??([^/?]+)|<([^>]+)>|{([^}]+)})/g);
  if (pathVariables) {
    let current = pathVariables.length;
    while (current-- > 0) {
      const variable = pathVariables[current];
      const key = variable[0] === ":" ? variable[1] === "?" ? variable.slice(2) : variable.slice(1) : variable.substring(1, variable.length - 1);
      if (parameters.hasOwnProperty(key)) {
        const value = parameters[key];
        if (value || value === 0) {
          templatedUrl = templatedUrl.replace(variable, value);
        } else {
          throw new RangeError(`Property ${key} of Argument parameters is required.`);
        }
      }
    }
  }
  templatedUrl = templatedUrl.replace(/\/\:\?[^/]+/g, "");
  if (searchStrings.length) {
    templatedUrl += (templatedUrl.indexOf("?") < 0 ? "?" : "&") + searchStrings.join("&");
  }
  return templatedUrl;
}

// src/js-dot/api/http/classes/http-request.ts
var HttpRequest = class {
  constructor(method, url, body, option) {
    this.method = method;
    this.url = url;
    this.body = body;
    this.option = option;
    this.path = url.split("/").slice(4).join("/");
  }
  response;
  option;
  path;
  next(response) {
    return this.dispatch(response);
  }
};

// src/js-dot/api/http/classes/http-response.ts
var HttpResponse = class _HttpResponse {
  request;
  originResponse;
  _intercepted;
  constructor(request, response) {
    this.request = {
      method: request.method,
      url: request.url,
      option: request.option
    };
    const originResponse = response?.originResponse || response;
    this.originResponse = originResponse;
    if (!(originResponse instanceof Response || originResponse instanceof _HttpResponse)) {
      if (!(typeof originResponse === "object" && Object.hasOwn(originResponse, "bodyUsed") && Object.hasOwn(originResponse, "body") && Object.hasOwn(originResponse, "status"))) {
        this._intercepted = true;
      }
    }
  }
  get url() {
    return this.request.url;
  }
  get status() {
    return this.originResponse.status;
  }
  get body() {
    return this._intercepted ? this.originResponse : this.originResponse.body;
  }
  get bodyUsed() {
    return this._intercepted || this.originResponse.bodyUsed;
  }
  get headers() {
    return this.originResponse.headers;
  }
  get ok() {
    return this._intercepted || this.originResponse.ok;
  }
  get redirected() {
    return this.originResponse.redirected;
  }
  get statusText() {
    return this.originResponse.statusText;
  }
  get type() {
    return this.originResponse.type;
  }
  get bytes() {
    return this.originResponse["bytes"];
  }
  arrayBuffer() {
    return Promise.resolve(void 0);
  }
  blob() {
    return this.originResponse.blob();
  }
  clone() {
    return this.originResponse.clone();
  }
  formData() {
    return this.originResponse.formData();
  }
  _parsePromises = [];
  _parsed;
  #getParse(type) {
    const {
      originResponse
    } = this;
    if (this._intercepted) {
      return Promise.resolve(this.originResponse);
    }
    const {
      _parsePromises
    } = this;
    if (this.bodyUsed) {
      const {
        _parsePromises: _parsePromises2,
        _parsed
      } = this;
      return new Promise((resolve, reject) => {
        _parsePromises2[_parsePromises2.length] = [resolve, reject];
      });
    }
    return originResponse[type]().then(result => {
      let current = _parsePromises.length;
      while (current-- > 0) {
        _parsePromises[current][0](result);
      }
      this._parsed = result;
      this._parsePromises = null;
      this[type] = this.#getSuccess.bind(this);
      return result;
    }).catch(reason => {
      let current = _parsePromises.length;
      while (current-- > 0) {
        _parsePromises[current][1](reason);
      }
      this._parsed = reason;
      this._parsePromises = null;
      this[type] = this.#getFail.bind(this);
      throw reason;
    });
  }
  #getSuccess(type) {
    return Promise.resolve(this._parsed);
  }
  #getFail() {
    return Promise.reject(this._parsed);
  }
  json() {
    return this.#getParse("json");
  }
  text() {
    return this.#getParse("text");
  }
};

// src/js-dot/api/http/classes/http-error.ts
var HttpError = class extends HttpResponse {
  get message() {
    return this.originResponse["message"];
  }
};

// src/js-dot/api/http/classes/http-client-base.ts
var HttpClientBase = class {
  _host = "";
  _firstSetHostResolvers = [];
  meta = {
    interceptors: {
      requests: [],
      responses: []
    }
  };
  credentials = "same-origin";
  constructor(host) {
    if (host) {
      this.setHost(host);
    }
  }
  onHostChanged() {
    const {
      _firstSetHostResolvers
    } = this;
    return _firstSetHostResolvers ? new Promise(resolve => {
      _firstSetHostResolvers[_firstSetHostResolvers.length] = resolve;
    }) : Promise.resolve();
  }
  setHost(host) {
    this._host = host;
    const {
      _firstSetHostResolvers
    } = this;
    if (_firstSetHostResolvers) {
      let current = _firstSetHostResolvers.length;
      while (current-- > 0) {
        _firstSetHostResolvers[current]();
      }
      this._firstSetHostResolvers = null;
    }
  }
  getHost() {
    return this._host;
  }
  getUrl(path) {
    return path.indexOf("://") > -1 ? path : this._host + path;
  }
  /**
   * Request
   * @param method
   * @param url
   * @param body
   * @param option
   */
  request(method, url, body, option) {
    return this._execute(method, url, body, option);
  }
  /**
   * Request <GET>
   * @param url
   * @param option
   */
  head(url, option) {
    return this.request("head", url, null, option);
  }
  /**
   * Request <GET>
   * @param url
   * @param option
   */
  get(url, option) {
    return this.request("get", url, null, option);
  }
  /**
   * Request <PATCH>
   * @param url
   * @param body
   * @param option
   */
  patch(url, body, option) {
    return this.request("patch", url, body, option);
  }
  /**
   * Request <POST>
   * @param url
   * @param body
   * @param option
   */
  post(url, body, option) {
    return this.request("post", url, body, option);
  }
  /**
   * Request <PUT>
   * @param url
   * @param body
   * @param option
   */
  put(url, body, option) {
    return this.request("put", url, body, option);
  }
  /**
   * Request <DELETE>
   * @param url
   * @param option
   */
  delete(url, option) {
    return this.request("delete", url, null, option);
  }
};

// src/js-dot/api/http/classes/http-headers.ts
var HttpHeaders = class _HttpHeaders {
  values = {};
  [Symbol.iterator]() {
    throw new Error("Method not implemented.");
  }
  constructor(init) {
    if (!init) {
      return;
    }
    this.values = init instanceof _HttpHeaders ? {
      ...init.values
    } : Array.isArray(init) ? Object.fromEntries(init) : init;
  }
  getSetCookie() {
    throw new Error("Method not implemented.");
  }
  entries() {
    return Object.entries(this.values);
  }
  keys() {
    return Object.keys(this.values);
  }
  append(name, value) {
    this.values[name] = value;
  }
  delete(name) {
    delete this.values[name];
  }
  get(name) {
    return this.values[name];
  }
  has(name) {
    return !!this.values[name];
  }
  set(name, value) {
    this.values[name] = value;
  }
  forEach(callback, thisArg) {
    const {
      values
    } = this;
    Object.keys(values).forEach(key => {
      callback.call(thisArg || this, values[key], key, this);
    });
  }
};

// src/js-dot/api/http/classes/http-requester-base.ts
import { parseUrl, promisify, toCamelCase, toKebabCase, toSnakeCase } from "@js-dot/core";
var HttpRequesterBase = class {
  //////////////////////////////
  // Inherit
  meta;
  //////////////////////////////
  // Un-inherit
  _polyfillCallback = null;
  _parameters = {};
  _preloadApis = [];
  _option;
  get method() {
    return this.meta.method;
  }
  get path() {
    return this.meta.path;
  }
  set path(path) {
    this.meta.path = path;
  }
  get url() {
    return this.meta.client.getUrl(this.meta.path);
  }
  constructor(client_or_path_or_method = "", method_or_path_or_option, path_or_option) {
    let client;
    let path;
    let method;
    let option = {};
    if (typeof client_or_path_or_method !== "string") {
      client = client_or_path_or_method;
      method = method_or_path_or_option || "GET";
      path = path_or_option || "";
    } else {
      switch (arguments.length) {
        case 1:
          path = client_or_path_or_method;
          break;
        case 2:
          if (typeof method_or_path_or_option === "string") {
            method = client_or_path_or_method;
            path = method_or_path_or_option;
          } else {
            path = client_or_path_or_method;
            path_or_option = method_or_path_or_option;
          }
          break;
        case 3:
          method = client_or_path_or_method;
          path = method_or_path_or_option;
          option = path_or_option;
      }
    }
    const meta = Object.assign({
      method: "GET",
      path: "/",
      credentials: "same-origin",
      transforms: {
        requests: [],
        responses: []
      },
      interceptors: {
        // requests: [$deduplicate_requestInterceptor],
        requests: [],
        responses: [],
        // lastResponses: [$deduplicate_responseInterceptor],
        lastResponses: []
      }
    }, {
      // @ts-ignore
      client,
      // @ts-ignore
      method,
      // @ts-ignore
      path
    });
    if (!meta.client) {
      const clientClass = this._getHttpClient();
      if (!clientClass["_instance_"]) {
        clientClass["_instance_"] = new clientClass();
      }
      meta.client = new clientClass();
    }
    this.meta = meta;
    this._option = option;
  }
  /**
   * @inheritDoc
   */
  get(path, option) {
    return new (this._getHttpRequester())(this.meta.client, "GET", addPath(this.path, path), option);
  }
  /**
   * @inheritDoc
   */
  post(path, option) {
    return new (this._getHttpRequester())(this.meta.client, "POST", addPath(this.path, path), option);
  }
  /**
   * @inheritDoc
   */
  put(path, option) {
    return new (this._getHttpRequester())(this.meta.client, "PUT", addPath(this.path, path), option);
  }
  /**
   * @inheritDoc
   */
  patch(path, option) {
    return new (this._getHttpRequester())(this.meta.client, "PATCH", addPath(this.path, path), option);
  }
  /**
   * @inheritDoc
   */
  delete(path, option) {
    return new this.constructor(this.meta.client, "DELETE", addPath(this.path, path), option);
  }
  url2(parameters) {
    return this.meta.client.getUrl(setTemplateUrl(this.meta.path, parameters));
  }
  request(parameters, body, option = {}) {
    if (!parameters) {
      parameters = {};
    }
    const inlineOption = option;
    const defaultOption = {
      ...this._option
    };
    defaultOption.headers = getHeaderValues(defaultOption.headers);
    inlineOption.headers = getHeaderValues(inlineOption.headers);
    option = {
      ...defaultOption,
      ...inlineOption
    };
    const {
      meta: {
        client,
        method,
        credentials,
        transforms: {
          requests: requestTransformers
        },
        interceptors: {
          requests: requestInterceptors
        }
      }
    } = this;
    let end = requestTransformers.length;
    let current = -1;
    if (!("credentials" in option)) {
      option.credentials = credentials;
    }
    const headersClone = {
      ...option.headers
    };
    while (++current < end) {
      const [transformType, transformCallback] = requestTransformers[current];
      switch (transformType) {
        case "request":
          const transformed = transformCallback({
            headers: headersClone,
            parameters,
            body
          });
          if (transformed.hasOwnProperty("headers")) {
            option.headers = getHeaderValues(transformed.headers);
          }
          if (transformed.hasOwnProperty("parameters")) {
            parameters = transformed.parameters;
          }
          if (transformed.hasOwnProperty("body")) {
            body = transformed.body;
          }
          break;
        case "parameters":
          parameters = transformCallback(parameters, {
            headers: headersClone,
            body
          });
          break;
        case "body":
          body = transformCallback(body, {
            headers: headersClone,
            parameters
          });
          break;
      }
    }
    const caseAs = option.hasOwnProperty("caseAs") ? option.caseAs : "kebab-case";
    const casing = {
      "kebab-case": toKebabCase,
      "camelCase": toCamelCase,
      "snake_case": toSnakeCase
    }[caseAs];
    for (let key in parameters) {
      if (parameters.hasOwnProperty(key) && key.startsWith("$")) {
        const casedKey = casing(key);
        if (casedKey !== key) {
          parameters[casedKey] = parameters[key];
          delete parameters[key];
        }
      }
    }
    option.parameters = parameters;
    const url = client.getUrl(setTemplateUrl(this.meta.path, parameters));
    let request = client.request(method, url, body, option);
    end = requestInterceptors.length;
    if (end) {
      let current2 = -1;
      while (++current2 < end) {
        const requestInterceptor = requestInterceptors[current2];
        const result = requestInterceptor(request);
        if (request === result) {
          continue;
        }
        if (result instanceof HttpRequest) {
          request = result;
          continue;
        }
        if (result instanceof HttpResponse) {
          request.response = result;
          return request.next(promisify(this._responseIntercept(request)));
        }
        if (result instanceof HttpError) {
          return request.reject(result);
        }
        if (result instanceof Promise) {
          return request.next(result.then(result_ => {
            request.response = result_;
            return promisify(this._responseIntercept(request));
          }).catch(reason => {
            request.response = new HttpError(request, reason);
            return promisify(this._responseIntercept(request));
          }));
        }
        return request.resolve(result);
      }
    }
    return this._execute(request, parameters, body, option);
  }
  _responseIntercept(request) {
    const {
      meta: {
        transforms: {
          responses: responseTransformers
        },
        interceptors: {
          responses: responseInterceptors,
          lastResponses: lastResponseInterceptors
        }
      }
    } = this;
    const allResponseInterceptors = [...responseInterceptors, ...lastResponseInterceptors];
    const responseInterceptorEnd = allResponseInterceptors.length;
    let current = -1;
    while (++current < responseInterceptorEnd) {
      const interceptor = allResponseInterceptors[current];
      const newResponse = interceptor(request.response);
      if (newResponse === request.response) {
        continue;
      }
      if (newResponse instanceof HttpResponse) {
        request.response = newResponse;
        continue;
      }
      if (newResponse instanceof HttpError) {
        return request.reject(newResponse);
      }
      request.response = newResponse;
    }
    return request.next(request.response);
  }
  pipe(...pipeOperators) {
    const end = pipeOperators.length;
    let current = -1;
    let result = this;
    while (++current < end) {
      const pipeOperator = pipeOperators[current];
      result = pipeOperator(result);
    }
    return result;
  }
  toString() {
    return `${this.method} ${this.path}`;
  }
};
var {
  isArray: IS_ARRAY
} = Array;
var {
  fromEntries: FROM_ENTRIES
} = Object;
function getHeaderValues(headers) {
  return headers instanceof HttpHeaders ? headers.values : IS_ARRAY(headers) ? FROM_ENTRIES(headers) : headers || {};
}
function addPath(before, after) {
  if (before) {
    if (after) {
      const {
        protocol,
        host,
        pathname,
        search,
        hash
      } = parseUrl(before);
      return (host ? `${protocol}//${host}` : "") + (pathname ? pathname.endsWith("/") && after.startsWith("/") ? pathname + after.slice(1) : pathname + after : after.startsWith("/") ? after : "/" + after) + (search || "") + (hash || "");
    }
    return before;
  }
  return after;
}

// src/js-dot/api/http/classes/dynamic-http-base.ts
var DynamicHttpBase = class {
  constructor(httpRequesterConstructor, httpClient) {
    this.httpRequesterConstructor = httpRequesterConstructor;
    this.httpClient = httpClient;
    this._httpRequestConstructor = httpRequesterConstructor;
    this.httpClient = httpClient;
  }
  _httpRoot;
  _httpRequestConstructor;
  /**
   * Default Http Root Path
   * @protected
   */
  httpRootPath;
  /**
   * Http Root Requester
   */
  get httpRoot() {
    return this._httpRoot || (this._httpRoot = this.httpClient ? new this._httpRequestConstructor(this.httpClient, "GET", this.httpRootPath) : new this._httpRequestConstructor(this.httpRootPath));
  }
  // protected httpRequester(method: HttpMethod): HttpRequester
  // protected httpRequester(client: HttpClient, method: HttpMethod, path: string): HttpRequester
  // protected httpRequester(client_or_path: HttpClient | string, method?: HttpMethod, path?: string){
  // 	return this._httpClient ?
  // 		new (this._httpRequestConstructor)(this._httpClient, method, path) :
  // 		new (this._httpRequestConstructor)(path);
  // }
};
export { DynamicHttpBase, HttpClientBase, HttpError, HttpHeaders, HttpRequest, HttpRequesterBase, HttpResponse, getHeaderValues, setTemplateUrl };
